import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { RowSelectionState } from "@tanstack/react-table";
import { FinancialDocumentListItem } from "@common/types/financialDocument";
import {
    FinancialDocumentSortValue,
    FinancialDocumentView,
    FinancialDocumentViewSchema,
} from "@common/types/views/financialDocumentViews";
import { useScope } from "@state/scopeContext.lib";
import { CommandIsland } from "@design-system/CommandIsland/CommandIsland";
import commandIslandStyles from "@design-system/CommandIsland/CommandIslandVariants.module.scss";
import { EmptyState } from "@design-system/EmptyState/EmptyState";
import { InfiniteScrollSentinel } from "@design-system/InfiniteScrollSentinel/InfiniteScrollSentinel";
import { BulkSelectionControl } from "@design-system/BulkSelectionControl/BulkSelectionControl";
import { Button } from "@design-system/Button/Button";
import { ViewTable } from "@design-system/ViewTable/ViewTable";
import { ColumnSettings } from "@design-system/ViewToolbar/ColumnSettings";
import { useWorkspaceUserCache } from "@hooks/useWorkspaceUserCache";
import { queryFinancialDocuments } from "@lib/financialDocument";
import { Loader } from "@design-system/Loader/Loader";
import { financialDocumentQueryKeys } from "../lib";
import { getSortExpression } from "../useFinancialDocumentSort";
import { TablePageHeader } from "../../pageHeader/TablePageHeader";
import { queryClient } from "../../../queryClient";
import {
    FinancialDocumentFilters,
    getDocumentFilters,
} from "../FinancialDocumentsPageFilters/lib";
import {
    financialDocumentSelectViewConfig,
    defaultDocumentSelectViews,
    DocumentSelectTableMeta,
    DocumentSelectVariant,
    VARIANT_CACHE_KEYS,
} from "./financialDocumentSelectViewConfig";
import styles from "./FinancialDocumentSelect.module.scss";

interface Props {
    variant: DocumentSelectVariant;
    cta: (document: FinancialDocumentListItem) => React.ReactNode;
    onPreviewed: (document: FinancialDocumentListItem) => void;
    onDocumentsChange: (documents: FinancialDocumentListItem[]) => void;
    filters: FinancialDocumentFilters;
    page: number;
    handleFiltersChange: (filters: FinancialDocumentFilters) => void;
    resetFilters: () => void;
    attachedDocumentIds?: number[];
    attachDocuments?: (documentIds: number[]) => void;
    getRowSelectionDisabledTooltip?: (
        document: FinancialDocumentListItem,
    ) => string | undefined;
}

const PAGE_SIZE = 50;

function isFinancialDocumentSelectRow(
    row: unknown,
): row is FinancialDocumentListItem {
    if (typeof row !== "object" || row === null) {
        return false;
    }
    if (!("id" in row) || !("fileName" in row)) {
        return false;
    }
    const { id, fileName } = row;
    return typeof id === "number" && typeof fileName === "string";
}

export const FinancialDocumentSelectContent: React.FC<Props> = ({
    variant,
    cta,
    onPreviewed,
    onDocumentsChange,
    filters,
    page,
    handleFiltersChange,
    resetFilters,
    attachedDocumentIds,
    attachDocuments,
    getRowSelectionDisabledTooltip,
}) => {
    const { activeWorkspaceKey } = useScope();
    const [scrollContainer, setScrollContainer] =
        useState<HTMLDivElement | null>(null);
    const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
    const isBulkSelectEnabled = !!attachDocuments;

    const defaultView = defaultDocumentSelectViews[variant];

    const [sort, setSort] = useState<FinancialDocumentSortValue>(
        FinancialDocumentSortValue.UPLOAD_DATE_DESC,
    );

    const [cachedView, setCachedView] =
        useWorkspaceUserCache<FinancialDocumentView>(
            VARIANT_CACHE_KEYS[variant],
            FinancialDocumentViewSchema,
        );
    const [selectView, setSelectView] = useState<FinancialDocumentView>(
        cachedView ?? defaultView,
    );

    const handleViewChange = useCallback(
        (view: FinancialDocumentView) => {
            setSelectView(view);
            setCachedView(view);
        },
        [setCachedView],
    );

    const view = useMemo(() => {
        if (!isBulkSelectEnabled) {
            return selectView;
        }
        return {
            ...selectView,
            options: {
                ...selectView.options,
                bulkActions: true,
                selection: true,
            },
        };
    }, [selectView, isBulkSelectEnabled]);

    const availableFilters = getDocumentFilters();

    const currentQueryParams = useMemo(
        () => ({
            page: page - 1,
            limit: PAGE_SIZE,
            filters,
            workspaceId: activeWorkspaceKey,
            sort: getSortExpression(sort),
        }),
        [activeWorkspaceKey, filters, page, sort],
    );

    const {
        data: financialDocumentsResponse,
        isLoading,
        hasNextPage,
        fetchNextPage,
        isFetchingNextPage,
    } = useInfiniteQuery({
        queryKey: financialDocumentQueryKeys.list(currentQueryParams),
        queryFn: ({ pageParam = 0 }) =>
            queryFinancialDocuments({
                ...currentQueryParams,
                page: pageParam,
            }),
        getNextPageParam: (lastPage, allPages) => {
            if (!allPages) {
                return 0;
            }
            // pageCount is absent on skipCount responses; fall back to a full-page check
            const hasNextPageToFetch =
                lastPage.pageCount !== undefined
                    ? lastPage.pageCount > allPages.length
                    : lastPage.data.length === PAGE_SIZE;
            if (hasNextPageToFetch) {
                return allPages.length;
            }
        },
        initialPageParam: 0,
    });

    useEffect(() => {
        queryClient.invalidateQueries({
            queryKey: financialDocumentQueryKeys.lists(),
        });
    }, []);

    const documents = useMemo(
        () =>
            financialDocumentsResponse?.pages.flatMap((p) => [...p.data]) ??
            null,
        [financialDocumentsResponse],
    );

    useEffect(() => {
        onDocumentsChange(documents ?? []);
    }, [documents, onDocumentsChange]);

    // Bump row identity for any document whose attachment status may have
    // changed. The ViewTable row is memoized on `row.original ===`, so without
    // a fresh reference the row (and its CTA) won't re-render when the
    // consumer updates `attachedDocumentIds`.
    const attachedIdSet = useMemo(
        () => new Set(attachedDocumentIds ?? []),
        [attachedDocumentIds],
    );
    const tableData = useMemo(() => {
        if (isLoading && !documents?.length) {
            return null;
        }
        if (!documents) {
            return [];
        }
        if (attachedDocumentIds === undefined) {
            return documents;
        }
        return documents.map((doc) =>
            attachedIdSet.has(doc.id) ? { ...doc } : doc,
        );
    }, [isLoading, documents, attachedDocumentIds, attachedIdSet]);

    const tableMeta: DocumentSelectTableMeta = useMemo(() => {
        const meta: DocumentSelectTableMeta = {
            callbacks: {
                cta,
                onPreviewed,
            },
        };
        if (getRowSelectionDisabledTooltip) {
            meta.getRowSelectionDisabledTooltip = (row) => {
                if (!isFinancialDocumentSelectRow(row)) {
                    return undefined;
                }
                return getRowSelectionDisabledTooltip(row);
            };
        }
        return meta;
    }, [cta, onPreviewed, getRowSelectionDisabledTooltip]);

    const canSelectDocument = useCallback(
        (document: FinancialDocumentListItem) =>
            !attachedIdSet.has(document.id),
        [attachedIdSet],
    );

    // Drop rows from the selection once they become attached externally (e.g.
    // via a row's own attach CTA), so the bulk bar count doesn't drift.
    useEffect(() => {
        if (!isBulkSelectEnabled) {
            return;
        }
        setRowSelection((prev) => {
            const next = Object.fromEntries(
                Object.entries(prev).filter(
                    ([id, isSelected]) =>
                        isSelected && !attachedIdSet.has(Number(id)),
                ),
            );
            return Object.keys(next).length === Object.keys(prev).length
                ? prev
                : next;
        });
    }, [attachedIdSet, isBulkSelectEnabled]);

    const clearSelection = useCallback(() => setRowSelection({}), []);

    // Reset the selection when the result set changes, otherwise the bulk bar
    // could count rows that are no longer visible and silently drop them on
    // attach (the selection is keyed by document id, not by position).
    const handleFiltersChangeAndClearSelection = useCallback(
        (nextFilters: FinancialDocumentFilters) => {
            clearSelection();
            handleFiltersChange(nextFilters);
        },
        [clearSelection, handleFiltersChange],
    );

    const handleSortChangeAndClearSelection = useCallback(
        (nextSort: FinancialDocumentSortValue) => {
            clearSelection();
            setSort(nextSort);
        },
        [clearSelection],
    );

    const selectedCount = useMemo(
        () => Object.values(rowSelection).filter(Boolean).length,
        [rowSelection],
    );

    const handleAddSelected = useCallback(() => {
        if (!attachDocuments || !tableData) {
            return;
        }
        const selectedDocuments = tableData.filter(
            (document) => rowSelection[document.id],
        );
        attachDocuments(selectedDocuments.map((document) => document.id));
        clearSelection();
    }, [attachDocuments, clearSelection, rowSelection, tableData]);

    const resetFiltersAndClearSelection = useCallback(() => {
        clearSelection();
        resetFilters();
    }, [clearSelection, resetFilters]);

    const emptyView = useCallback(
        () => (
            <EmptyState
                header="The filters don't match any of the documents."
                action={resetFiltersAndClearSelection}
            />
        ),
        [resetFiltersAndClearSelection],
    );

    const hasSelection = selectedCount > 0;

    const footer = useMemo(() => {
        if (!isBulkSelectEnabled || !hasSelection) {
            return undefined;
        }
        return (
            <CommandIsland
                sticky
                className={commandIslandStyles.bulkActionsStickyFooter}
            >
                <BulkSelectionControl
                    selectedCount={selectedCount}
                    onClearSelection={clearSelection}
                    size="md"
                />
                <Button variant="primary" size="md" onClick={handleAddSelected}>
                    Add
                </Button>
            </CommandIsland>
        );
    }, [
        isBulkSelectEnabled,
        hasSelection,
        selectedCount,
        clearSelection,
        handleAddSelected,
    ]);

    const appendContent = useMemo(
        () => (
            <>
                <InfiniteScrollSentinel
                    scrollRoot={scrollContainer}
                    onVisible={() => fetchNextPage()}
                    disabled={!hasNextPage || isFetchingNextPage}
                />
                {isFetchingNextPage && <Loader />}
            </>
        ),
        [scrollContainer, hasNextPage, isFetchingNextPage, fetchNextPage],
    );

    return (
        <main className={styles.container}>
            <TablePageHeader
                isFullPage
                search={filters.search}
                onSearchChange={(value) => {
                    handleFiltersChangeAndClearSelection({
                        search: value ?? "",
                    });
                }}
                filters={filters}
                onFiltersChange={handleFiltersChangeAndClearSelection}
                availableFilters={availableFilters}
                shouldShowNoAccountOption
            >
                <ColumnSettings
                    view={view}
                    onViewChange={handleViewChange}
                    viewConfig={financialDocumentSelectViewConfig}
                    defaultView={defaultView}
                />
            </TablePageHeader>

            <ViewTable
                className={styles.selectViewTable}
                data={tableData}
                view={view}
                config={financialDocumentSelectViewConfig}
                tableMeta={tableMeta}
                sort={sort}
                onSortChange={handleSortChangeAndClearSelection}
                emptyView={emptyView}
                appendContent={appendContent}
                footer={footer}
                onScrollContainerMount={setScrollContainer}
                enableRowSelection={
                    isBulkSelectEnabled ? canSelectDocument : false
                }
                rowSelection={isBulkSelectEnabled ? rowSelection : undefined}
                onRowSelectionChange={
                    isBulkSelectEnabled ? setRowSelection : undefined
                }
            />
        </main>
    );
};
